import { useCallback, useState } from 'react';

import { useSearchQuoteItems } from '@frontend/api';

import ExpandableFilterList from '../components/ExpandableFilterList';
import QuoteItemPartMatchCell from '../components/QuoteItemPartMatchCell';
import SearchResultList from '../components/SearchResultList';
import Searching from '../components/Searching';
import { TABS } from '../constants';
import { useSearchDataContext } from '../context';
import {
  buildFilterSeeMore,
  buildFilterUpdate,
} from '../utils/buildFilterUpdate';

const ALWAYS_VISIBLE_FIELDS = [
  'partNumber',
  'quoteNumber',
  'partName',
  'revision',
  'process',
  'material',
  'finishes',
];

const QuoteItemResultList = () => {
  const { userHasTyped, query, activeTab } = useSearchDataContext();

  const [paginateFrom, setPaginateFrom] = useState(
    query.quoteItems.paginateFrom,
  );
  const [filters, setFilters] = useState(query.quoteItems.filters);

  const { data: quoteItems } = useSearchQuoteItems(
    {
      ...query.quoteItems,
      paginateFrom,
      filters,
    },
    {
      queryOptions: {
        keepPreviousData: true,
        enabled: activeTab === TABS.QUOTE_ITEMS,
      },
    },
  );

  const onProcessChange = useCallback(
    (process) => buildFilterUpdate(setFilters, 'process')(process),
    [],
  );

  const onProcessSeeMore = useCallback(
    () => buildFilterSeeMore(setFilters, 'process')(),
    [],
  );

  const onMaterialChange = useCallback(
    (material) => buildFilterUpdate(setFilters, 'materials')(material),
    [],
  );

  const onMaterialSeeMore = useCallback(
    () => buildFilterSeeMore(setFilters, 'materials')(),
    [],
  );

  const onFinishChange = useCallback(
    (finish) => buildFilterUpdate(setFilters, 'finishes')(finish),
    [],
  );

  const onFinishSeeMore = useCallback(
    () => buildFilterSeeMore(setFilters, 'finishes')(),
    [],
  );

  if (userHasTyped) {
    return <Searching />;
  }

  return (
    <SearchResultList
      data={quoteItems}
      aliases={{
        notesToCustomer: "Manufacturer's Notes",
        privateNotes: 'Internal Notes',
      }}
      fsHide={['notesToCustomer', 'partDescription']}
      tabKey={TABS.QUOTE_ITEMS}
      labelPlural="Quote Items"
      labelSingular="Quote Item"
      matchCell={(result) => (
        <QuoteItemPartMatchCell quoteItemResult={result} />
      )}
      highlightConfig={{
        alwaysVisibleFields: ALWAYS_VISIBLE_FIELDS,
        children: {
          contact: [
            {
              field: 'contactFullName',
              key: 'contactFullName',
              label: 'Contact Name',
              showOnlyIfHighlighted: true,
            },
            {
              field: 'accountName',
              key: 'accountName',
              label: 'Account Name',
              showOnlyIfHighlighted: true,
            },
            {
              field: 'contactEmail',
              key: 'contactEmail',
              label: 'Contact Email',
              showOnlyIfHighlighted: true,
            },
            {
              field: 'contactPhone',
              key: 'contactPhone',
              label: 'Contact Phone',
              showOnlyIfHighlighted: true,
            },
          ],
        },
      }}
      filters={
        <>
          <ExpandableFilterList
            agg={quoteItems?.aggs.processesAgg.processes}
            label="Process"
            selectedKeys={filters.process.values}
            onClick={onProcessChange}
            onSeeMore={onProcessSeeMore}
          />
          <ExpandableFilterList
            agg={quoteItems?.aggs.materialsAgg.materials}
            label="Materials"
            selectedKeys={filters.materials.values}
            onClick={onMaterialChange}
            onSeeMore={onMaterialSeeMore}
          />
          <ExpandableFilterList
            agg={quoteItems?.aggs.finishesAgg.finishes}
            label="Finishes"
            selectedKeys={filters.finishes.values}
            onClick={onFinishChange}
            onSeeMore={onFinishSeeMore}
          />
        </>
      }
      paginationInfo={{
        paginateFrom,
        paginateSize: query.quoteItems.paginateSize,
      }}
      onPaginate={(pagination) => setPaginateFrom(pagination.paginateFrom)}
    />
  );
};

export default QuoteItemResultList;
